/**
 * PhotosTab Component
 *
 * Simplified photo management for guide profile:
 * - Profile Photo URL
 * - Cover Photo URL
 *
 * Epic: MVP 1.21.3 - Guide Dashboard
 * Story: US-1.21.3-12: 5-Tab Profile Editor (8 SP)
 *
 * Note: Full photo upload with S3 integration can be added later.
 * For now, allows URL input for profile and cover photos.
 *
 * Props:
 * - profile: object with current profile data
 * - onChange: function(field, value) - Called when field changes
 * - onSave: function() - Called when save button clicked
 * - isSaving: boolean
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Save as _Save, Image, Upload, Save } from 'lucide-react';
import { getGuidePhotoUrl } from '../../../utils/guideDataAdapters';
import Avatar from '../../common/Avatar';

export default function PhotosTab({
  profile,
  onChange,
  onSave,
  isSaving = false
}) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Profile Photos</h2>
          <p className="mt-1 text-sm text-gray-500">
            Add photos to showcase your services and build trust with customers
          </p>
        </div>

        {/* Profile Photo */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Photo</h3>

          <div className="flex items-start space-x-6">
            {/* Preview */}
            <Avatar
              src={getGuidePhotoUrl(profile, 'profile')}
              name={`${profile.first_name || ''} ${profile.last_name || ''}`.trim()}
              size="3xl"
              className="flex-shrink-0 border-2 border-gray-200"
            />

            {/* Input */}
            <div className="flex-1">
              <label htmlFor="profile_photo_url" className="block text-sm font-medium text-gray-700 mb-1">
                Photo URL
              </label>
              <input
                type="url"
                id="profile_photo_url"
                value={profile.profile_photo_url || ''}
                onChange={(e) => onChange('profile_photo_url', e.target.value)}
                placeholder="https://example.com/photo.jpg"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-2 text-xs text-gray-500">
                Enter a URL to your profile photo. Square photos (1:1 ratio) work best.
              </p>
              <div className="mt-3 flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <Upload className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <p className="text-xs text-blue-800">
                  <strong>Coming Soon:</strong> Direct photo upload from your device
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Cover Photo */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Cover Photo</h3>

          <div className="space-y-4">
            {/* Preview */}
            {getGuidePhotoUrl(profile, 'cover') && (
              <div className="w-full h-48 rounded-lg overflow-hidden border-2 border-gray-200">
                <img
                  src={getGuidePhotoUrl(profile, 'cover')}
                  alt="Cover"
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {!getGuidePhotoUrl(profile, 'cover') && (
              <div className="w-full h-48 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center">
                <div className="text-center text-white">
                  <Image className="w-12 h-12 mx-auto mb-2 opacity-75" />
                  <p className="text-sm font-medium">No cover photo set</p>
                </div>
              </div>
            )}

            {/* Input */}
            <div>
              <label htmlFor="cover_photo_url" className="block text-sm font-medium text-gray-700 mb-1">
                Cover Photo URL
              </label>
              <input
                type="url"
                id="cover_photo_url"
                value={profile.cover_photo_url || ''}
                onChange={(e) => onChange('cover_photo_url', e.target.value)}
                placeholder="https://example.com/cover.jpg"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter a URL to your cover photo. Landscape photos (16:9 or 2:1 ratio) work best.
              </p>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Photo Tips</h4>
          <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
            <li>Use high-quality, well-lit photos</li>
            <li>Profile photo: Show your face clearly (helps build trust)</li>
            <li>Cover photo: Showcase your expertise or beautiful scenery from your trips</li>
            <li>Avoid photos with text overlays or watermarks</li>
            <li>Make sure you have rights to use the photos</li>
          </ul>
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

PhotosTab.propTypes = {
  profile: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};
